import {
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MedicalPaOrdersState } from "../hooks/useMedicalPaOrdersTableData";
import {
  generateMedicalPaOrdersAlgoliaTableDataForAdmin,
  generateMedicalPaOrdersAlgoliaTableDataForNonAdmin,
} from "../utils/medicalPaOrdersTable";

/**
 * Fetches the next page of medical PA orders from Postgres and appends the results
 * to the existing state and table data. Shows an inline loader during the fetch.
 *
 * @param payload - Search parameters including the target page number
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setMedicalPaOrdersState - State setter for loading/data lifecycle (uses showinLineLoader)
 * @param setTableData - State setter that appends new rows to existing table data
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  setMedicalPaOrdersState((prev) => ({ ...prev, showinLineLoader: true }));

  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;

    setMedicalPaOrdersState((prev) => ({
      ...prev,
      data: {
        ...data,
        hits: [...(prev.data?.hits ?? []), ...(data.hits ?? [])],
      },
    }));

    const newTableData =
      isCurrentUserAdmin() || isCurrentUserSuperAdmin()
        ? generateMedicalPaOrdersAlgoliaTableDataForAdmin(
            data.hits ?? [],
            authStatusOptions,
          )
        : generateMedicalPaOrdersAlgoliaTableDataForNonAdmin(
            data.hits ?? [],
            authStatusOptions,
          );

    setTableData((prev) => [...prev, ...newTableData]);
  } catch (error) {
    setMedicalPaOrdersState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setMedicalPaOrdersState((prev) => ({ ...prev, showinLineLoader: false }));
  }
};
